import React, { forwardRef } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import StandardLogo from '../../assets/virtualpat-improved-standard.svg';
import DarkLogo from '../../assets/virtualpat-improved-dark.svg';
import VIcon from '../../assets/virtualpat-v-icon.svg';

/**
 * VirtualPATLogo - A comprehensive, responsive logo component
 *
 * Features:
 * - Mobile-first responsive design
 * - Automatic theme-aware logo switching (light/dark)
 * - Multiple variants (full, compact, icon-only)
 * - Optimized SVG assets
 * - Accessible with proper alt text
 * - Integration with VirtualPAT theme system
 *
 * @param {Object} props
 * @param {'full'|'compact'|'icon'} props.variant - Logo variant to display
 * @param {'small'|'medium'|'large'|'auto'} props.size - Size preset or auto-responsive
 * @param {number} props.height - Custom height in pixels (overrides size)
 * @param {number} props.width - Custom width in pixels (overrides size)
 * @param {boolean} props.forceDark - Force dark logo regardless of theme
 * @param {boolean} props.forceLight - Force light logo regardless of theme
 * @param {string} props.alt - Custom alt text for accessibility
 * @param {Object} props.sx - Additional MUI sx styling
 * @param {Function} props.onClick - Click handler
 * @param {string} props.component - Component to render as (default: 'img')
 */
const VirtualPATLogo = forwardRef(
  (
    {
      variant = 'full',
      size = 'auto',
      height,
      width,
      forceDark = false,
      forceLight = false,
      alt = 'VirtualPAT Logo',
      sx = {},
      onClick,
      component = 'img',
      ...props
    },
    ref
  ) => {
    const muiTheme = useTheme();
    const { mode: themeMode } = useCustomTheme() || { mode: 'light' };

    // Responsive breakpoints
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(muiTheme.breakpoints.between('sm', 'md'));
    const isDesktop = useMediaQuery(muiTheme.breakpoints.up('md'));

    // Determine which logo to use based on theme and forced modes
    const isDarkMode = forceDark || (!forceLight && themeMode === 'dark');

    // Select logo source based on variant and theme
    const getLogoSrc = () => {
      switch (variant) {
        case 'icon':
          return VIcon;
        case 'compact':
        case 'full':
        default:
          return isDarkMode ? DarkLogo : StandardLogo;
      }
    };

    // Define responsive size presets
    const getSizeStyles = () => {
      if (height || width) {
        return {
          height: height ? `${height}px` : 'auto',
          width: width ? `${width}px` : 'auto',
        };
      }

      const sizeMap = {
        small: {
          mobile: { height: 28, width: 'auto' },
          tablet: { height: 32, width: 'auto' },
          desktop: { height: 36, width: 'auto' },
        },
        medium: {
          mobile: { height: 36, width: 'auto' },
          tablet: { height: 40, width: 'auto' },
          desktop: { height: 44, width: 'auto' },
        },
        large: {
          mobile: { height: 44, width: 'auto' },
          tablet: { height: 52, width: 'auto' },
          desktop: { height: 60, width: 'auto' },
        },
        auto: {
          mobile: variant === 'icon' ? { height: 32, width: 32 } : { height: 32, width: 'auto' },
          tablet: variant === 'icon' ? { height: 40, width: 40 } : { height: 40, width: 'auto' },
          desktop: variant === 'icon' ? { height: 48, width: 48 } : { height: 48, width: 'auto' },
        },
      };

      const currentSize = sizeMap[size] || sizeMap.auto;

      if (isMobile) return currentSize.mobile;
      if (isTablet) return currentSize.tablet;
      return currentSize.desktop;
    };

    // Icon-only variant adjustments
    const isIconVariant = variant === 'icon';

    const styles = {
      display: 'inline-block',
      ...getSizeStyles(),
      objectFit: 'contain',
      transition: muiTheme.transitions.create(['opacity'], {
        duration: muiTheme.transitions.duration.short,
      }),
      cursor: onClick ? 'pointer' : 'default',
      // Icon-specific styles
      ...(isIconVariant && {
        borderRadius: muiTheme.shape.borderRadius,
      }),
      // Hover effects
      ...(onClick && {
        '&:hover': {
          opacity: 0.8,
        },
        '&:active': {
          opacity: 0.7,
        },
      }),
      // Theme-specific filters for better contrast
      ...(isDarkMode &&
        !isIconVariant && {
          filter: 'brightness(1.1) contrast(1.05)',
        }),
      // Custom styles override
      ...sx,
    };

    // Accessibility enhancements
    const accessibilityProps = {
      alt,
      role: onClick ? 'button' : 'img',
      tabIndex: onClick ? 0 : undefined,
      'aria-label': alt,
      // Keyboard navigation for clickable logos
      ...(onClick && {
        onKeyDown: e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick(e);
          }
        },
      }),
    };

    return (
      <Box
        ref={ref}
        component={component}
        src={getLogoSrc()}
        onClick={onClick}
        sx={styles}
        {...accessibilityProps}
        {...props}
      />
    );
  }
);

VirtualPATLogo.displayName = 'VirtualPATLogo';

// Preset configurations for common use cases
export const LogoPresets = {
  // Header logo configurations
  header: {
    mobile: { variant: 'compact', size: 'medium', height: 40 },
    desktop: { variant: 'full', size: 'large', height: 50 },
  },
  // Sidebar logo configurations
  sidebar: {
    expanded: { variant: 'full', size: 'medium' },
    collapsed: { variant: 'icon', size: 'medium' },
  },
  // Login page logo configurations
  login: {
    variant: 'full',
    size: 'large',
    height: 60,
  },
  // Footer logo configurations
  footer: {
    variant: 'compact',
    size: 'small',
  },
  // Card/avatar logo configurations
  avatar: {
    variant: 'icon',
    size: 'small',
  },
};

// Convenience components for common use cases
export function HeaderLogo(props) {
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
  const preset = isMobile ? LogoPresets.header.mobile : LogoPresets.header.desktop;

  return <VirtualPATLogo {...preset} {...props} />;
}

export function SidebarLogo({ collapsed, ...props }) {
  const preset = collapsed ? LogoPresets.sidebar.collapsed : LogoPresets.sidebar.expanded;

  // When collapsed, ensure we use the icon variant with proper sizing
  if (collapsed) {
    return (
      <VirtualPATLogo 
        variant="icon"
        size="medium"
        height={40}
        width={40}
        {...props} 
      />
    );
  }

  return <VirtualPATLogo {...preset} {...props} />;
}

export function LoginLogo(props) {
  const { mode: themeMode } = useCustomTheme() || { mode: 'light' };
  
  // Use dark logo (which has lighter text) when in dark mode
  return (
    <VirtualPATLogo 
      {...LogoPresets.login} 
      forceDark={themeMode === 'dark'}
      {...props} 
    />
  );
}

export function FooterLogo(props) {
  return <VirtualPATLogo {...LogoPresets.footer} {...props} />;
}

export function AvatarLogo(props) {
  return <VirtualPATLogo {...LogoPresets.avatar} {...props} />;
}

export default VirtualPATLogo;
